import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {useToast} from './Toast';
import {setWelcomePagePreference} from '../store/slices/authThunks';
import {appUserSelector, userDisplayNameSelector} from '../store/selectors/auth.selectors';
import {currentProjectSelector} from '../store/selectors/projects.selectors';
import {transcriptionQuotaSelector} from '../store/selectors/usage.selectors';
import {isProSelector} from '../store/selectors/entitlement.selectors';
import {formatDurationCompact} from '../lib/audioMetadata';
import WelcomeGuide from './welcome/WelcomeGuide';
import {useVideoEditorPreference} from './useVideoEditorPreference';

/**
 * The onboarding screen, and the only page that can turn itself off.
 *
 * It hands off to the workspace rather than the project list: the workspace is where every piece of
 * work now starts, and a user who has just signed up has exactly one project anyway.
 *
 * `?show=1` is an explicit visit — the profile menu's "Getting started" row — and it beats the
 * dismissal. `welcome_page: false` means "don't land me here after signing in", not "never show me
 * this again", so the page still renders when it is asked for by name. It is a query param rather
 * than router state so that a reload or a pasted link still shows the page instead of bouncing.
 */
const WelcomeContainer: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {showToast} = useToast();

  const appUser = useAppSelector(appUserSelector);
  const displayName = useAppSelector(userDisplayNameSelector);
  const currentProject = useAppSelector(currentProjectSelector);
  const quota = useAppSelector(transcriptionQuotaSelector);
  const isPro = useAppSelector(isProSelector);

  const [isDismissing, setIsDismissing] = useState(false);
  const videoEditorPreference = useVideoEditorPreference();

  const [searchParams] = useSearchParams();
  const wasRequestedExplicitly = searchParams.get('show') === '1';

  // Unknown means show it: a profile read that hasn't landed yet must not flash the workspace.
  const isDismissed = appUser?.welcome_page === false;
  const shouldShowWelcome = wasRequestedExplicitly || !isDismissed;

  // Project-scoped URLs when a project is selected, so the page hands off to the same place the
  // header's links do rather than dropping the user into the Default Project.
  const projectPath = useCallback((suffix: string) => (
    currentProject?.id ? `/projects/${currentProject.id}${suffix}` : suffix
  ), [currentProject?.id]);

  const workspaceHref = projectPath('/workspace');

  // A user who has dismissed this page still has /welcome in their history and as the post-login
  // redirect, so the page bounces them on rather than re-showing something they turned off. In an
  // effect, not during render — navigating mid-render is a React warning and loses the redirect on
  // a re-render.
  useEffect(() => {
    if (!shouldShowWelcome) {
      navigate(workspaceHref, {replace: true});
    }
  }, [shouldShowWelcome, navigate, workspaceHref]);

  const handleDismiss = async () => {
    setIsDismissing(true);

    try {
      await dispatch(setWelcomePagePreference(false)).unwrap();
      showToast('Welcome page dismissed', 'success');
    } catch (error) {
      console.error('Failed to save welcome page preference:', error);
      showToast(`Failed to save preference: ${error}`, 'error');
    } finally {
      setIsDismissing(false);
      // Either way the user asked to move on, so a failed preference save does not strand them here.
      navigate(workspaceHref, {replace: true});
    }
  };

  if (!shouldShowWelcome) {
    return null;
  }

  const quotaLabel = !quota.isKnown
    ? null
    : quota.secondsRemaining === null
      ? 'Unlimited'
      : formatDurationCompact(Math.max(quota.secondsRemaining, 0));

  // "Inactive", not "Free": nobody can sign up into a free tier any more, so no active plan means a
  // lapsed subscription rather than a tier the user chose.
  return (
    <WelcomeGuide
      displayName={displayName}
      quotaLabel={quotaLabel}
      quotaLevel={quota.level}
      planLabel={isPro ? 'Pro' : 'Inactive'}
      workspaceHref={workspaceHref}
      wordlistsHref={projectPath('/wordlists')}
      canDismiss={!isDismissed}
      isDismissing={isDismissing}
      onGetStarted={() => navigate(workspaceHref)}
      onDismiss={handleDismiss}
      videoEditor={videoEditorPreference.videoEditor}
      onSelectVideoEditor={videoEditorPreference.choose}
      isSavingVideoEditor={videoEditorPreference.isSaving}
    />
  );
};

export default WelcomeContainer;
